import { initGoogleClient } from "./config";
import { authManagerInstance, AuthProvider } from "../values";

export const fetchGmailEmails = async (account, setLoading, setEmails, setError, folder = 'inbox') => {
    try {
        if (!account) {
            throw new Error("Gmail account is undefined or null");
        }
        setLoading(true);
        setEmails([]);
        if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
                throw new Error("Gmail API client not initialized properly");
            }
        }

        if (window.gapi.auth2) {
            const authInstance = window.gapi.auth2.getAuthInstance();
            if (!authInstance) {
                throw new Error("Google Auth instance not available");
            }

            const currentUser = authInstance.currentUser.get();
            if (!currentUser) {
                throw new Error("No Google user found");
            }

            if (currentUser.isSignedIn()) {
                const profile = currentUser.getBasicProfile();
                if (!profile) {
                    throw new Error("Unable to get user profile");
                }

                const currentEmail = profile.getEmail();
                let accountEmail;

                if (typeof account === 'string') {
                    const accountObj = authManagerInstance.getAccounts(AuthProvider.GOOGLE)
                        .find(acc => acc.id === account);
                    accountEmail = accountObj?.userData?.email;
                } else if (account.userData) {
                    accountEmail = account.userData.email;
                } else if (account.email) {
                    accountEmail = account.email;
                }

                if (accountEmail && currentEmail !== accountEmail) {
                    setError(`To view emails for ${accountEmail}, please sign out and sign in with that account. Current account: ${currentEmail}`);
                    return;
                }
            } else {
                throw new Error("No Google user is signed in");
            }
        }

        let labelQuery = '';
        switch (folder) {
            case 'inbox':
                labelQuery = 'in:inbox';
                break;
            case 'junk':
            case 'spam':
                labelQuery = 'in:spam';
                break;
            case 'sent':
                labelQuery = 'in:sent';
                break;
            case 'drafts':
                labelQuery = 'in:drafts';
                break;
            case 'trash':
                labelQuery = 'in:trash';
                break;
            case 'archive':
                labelQuery = '-in:inbox -in:spam -in:trash';
                break;
            default:
                labelQuery = 'in:inbox';
                break;
        }

        const response = await window.gapi.client.gmail.users.messages.list({
            userId: 'me',
            maxResults: 75,
            q: labelQuery
        });
        const emailIds = response?.result?.messages || [];
        if (emailIds.length === 0) {
            setEmails([]);
            return;
        }
        const emailPromises = emailIds.map(({ id }) =>
            window.gapi.client.gmail.users.messages.get({
                userId: 'me',
                id,
                format: 'full'
            })
        );

        const emailsData = await Promise.all(emailPromises);
        const transformedEmails = emailsData.map(({ result }) => {
            if (!result) return null;
            const headers = result.payload?.headers || [];
            const fromHeader = headers.find(header => header.name === 'From');
            const subjectHeader = headers.find(header => header.name === 'Subject');
            const toHeaders = headers.filter(header => header.name === 'To');
            const decodeBase64 = (data) => {
                if (!data) return '';
                let input = data.replace(/-/g, '+').replace(/_/g, '/');
                const pad = input.length % 4;
                if (pad) {
                    input += new Array(5 - pad).join('=');
                }

                try {
                    const raw = atob(input);
                    const utf8Decoder = new TextDecoder('utf-8');
                    const bytes = new Uint8Array(raw.length);
                    for (let i = 0; i < raw.length; i++) {
                        bytes[i] = raw.charCodeAt(i);
                    }

                    return utf8Decoder.decode(bytes);
                } catch (e) {
                    try {
                        return atob(input);
                    } catch (fallbackError) {
                        return '';
                    }
                }
            };

            const getBody = (part) => {
                if (!part) return '';
                let plainText = '';
                let htmlContent = '';

                if (part.body && part.body.data) {
                    if (part.mimeType === 'text/html') {
                        htmlContent = decodeBase64(part.body.data);
                        return htmlContent;
                    } else if (part.mimeType === 'text/plain') {
                        plainText = decodeBase64(part.body.data);
                    }
                }

                if (part.parts) {
                    for (const subPart of part.parts) {
                        if (subPart.mimeType === 'text/html' && subPart.body && subPart.body.data) {
                            htmlContent = decodeBase64(subPart.body.data);
                            return htmlContent;
                        } else if (subPart.mimeType === 'text/plain' && subPart.body && subPart.body.data) {
                            plainText = decodeBase64(subPart.body.data);
                        }
                        const nestedBody = getBody(subPart);
                        if (nestedBody) {
                            if (nestedBody.includes('<') && nestedBody.includes('>')) {
                                return nestedBody;
                            } else if (!htmlContent) {
                                plainText = nestedBody;
                            }
                        }
                    }
                }
                return htmlContent || plainText;
            };

            const body = result.payload ? getBody(result.payload) : '';
            const attachments = [];
            const extractAttachments = (part) => {
                if (!part) return;
                if (part.filename && part.filename.length > 0 && part.body) {
                    attachments.push({
                        id: part.body.attachmentId,
                        name: part.filename,
                        mimeType: part.mimeType,
                        size: part.body.size || 0,
                    });
                }

                if (part.parts) {
                    part.parts.forEach(extractAttachments);
                }
            };

            if (result.payload) {
                extractAttachments(result.payload);
            }

            return {
                id: result.id,
                threadId: result.threadId,
                body: body,
                fullEmail: result,
                message: result.snippet || '',
                read: !result.labelIds?.includes('UNREAD'),
                name: fromHeader?.value || 'Unknown Sender',
                subject: subjectHeader?.value || 'No Subject',
                attachments: attachments.length > 0 ? attachments : null,
                date: new Date(parseInt(result.internalDate || Date.now())).toLocaleDateString(),
                to: toHeaders.map(h => h.value).join(', ') || '',
                time: new Date(parseInt(result.internalDate || Date.now())).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                imageUrl: result.photoUrl || 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg',
                account: account,
                provider: AuthProvider.GOOGLE,
                folder: folder,
            };
        }).filter(email => email !== null);
        setEmails(transformedEmails);
    } catch (error) {
        if (error.status === 401 || error.message?.includes('auth') || error.message?.includes('sign')) {
            try {
                await initGoogleClient();
                if (window.gapi && window.gapi.auth2) {
                    const authInstance = window.gapi.auth2.getAuthInstance();
                    if (authInstance) {
                        const currentUser = authInstance.currentUser.get();
                        if (!currentUser.isSignedIn()) {
                            setError("Google session expired. Please sign in again.");
                        } else {
                            setError("Authentication error. Please try again.");
                        }
                    }
                }
            } catch (refreshError) {
                setError(`Google authentication error: ${refreshError.message}`);
            }
        } else {
            setError(`Failed to fetch Gmail emails: ${error.message}`);
        }

        setEmails([]);
    } finally {
        setLoading(false);
    }
};

export const getGmailAttachmentContent = async (account, messageId, attachmentId) => {
    try {
        if (!messageId || !attachmentId) {
            throw new Error("Message ID and Attachment ID are required");
        }

        if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
                throw new Error("Gmail API client not initialized");
            }
        }

        const response = await window.gapi.client.gmail.users.messages.attachments.get({
            userId: 'me',
            messageId: messageId,
            id: attachmentId,
        });

        if (!response || !response.result || !response.result.data) {
            throw new Error("No attachment data received");
        }

        let base64Data = response.result.data.replace(/-/g, '+').replace(/_/g, '/');
        while (base64Data.length % 4) {
            base64Data += '=';
        }

        const mimeType = response.result.mimeType || 'application/octet-stream';
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        return {
            contentUrl: blobUrl,
            contentType: mimeType,
            size: bytes.length,
            isBlob: true,
            rawBlob: blob,
        };
    } catch (error) {
        throw new Error(`Failed to load attachment: ${error.message}`);
    }
};

export const deleteGmailEmail = async (emailId, account, setLoading, setError) => {
    try {
        if (!emailId) {
            throw new Error("Email ID is required");
        }

        if (!account) {
            throw new Error("Gmail account is undefined or null");
        }

        setLoading(true);
        if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
                throw new Error("Gmail API client not initialized properly");
            }
        }
        if (!window.gapi.auth2) {
            throw new Error("Google Auth2 not initialized");
        }

        const authInstance = window.gapi.auth2.getAuthInstance();
        if (!authInstance) {
            throw new Error("Google Auth instance not available");
        }

        const currentUser = authInstance.currentUser.get();
        if (!currentUser) {
            throw new Error("No Google user found");
        }

        if (currentUser.isSignedIn()) {
            const profile = currentUser.getBasicProfile();
            if (!profile) {
                throw new Error("Unable to get user profile");
            }
            const currentEmail = profile.getEmail();
            let accountEmail;
            if (typeof account === 'string') {
                const accountObj = authManagerInstance.getAccounts(AuthProvider.GOOGLE)
                    .find(acc => acc.id === account);
                accountEmail = accountObj?.userData?.email;
            } else if (account.userData) {
                accountEmail = account.userData.email;
            } else if (account.email) {
                accountEmail = account.email;
            }

            if (accountEmail && currentEmail !== accountEmail) {
                setError(`To delete emails for ${accountEmail}, please sign out and sign in with that account`);
                return false;
            }
        } else {
            throw new Error("No Google user is signed in");
        }
        await window.gapi.client.gmail.users.messages.trash({
            userId: 'me',
            id: emailId,
        });
        return true;
    } catch (error) {
        if (error.status === 401 || error.message?.includes('auth') || error.message?.includes('sign')) {
            try {
                await initGoogleClient();
                if (window.gapi && window.gapi.auth2) {
                    const authInstance = window.gapi.auth2.getAuthInstance();
                    if (authInstance) {
                        const currentUser = authInstance.currentUser.get();
                        if (!currentUser.isSignedIn()) {
                            setError("Google session expired. Please sign in again.");
                        } else {
                            setError("Authentication error. Please try again.");
                        }
                    }
                }
            } catch (refreshError) {
                setError(`Google authentication error: ${refreshError.message}`);
            }
        } else {
            setError(`Failed to delete Gmail email: ${error.message}`);
        }
        return false;
    } finally {
        setLoading(false);
    }
};

export const sendGmailReply = async (account, threadId, emailId, replyContent, setLoading, setError) => {
    try {
        if (!threadId || !emailId) {
            throw new Error("Thread ID and Email ID are required");
        }

        if (!account) {
            throw new Error("Gmail account is undefined or null");
        }

        if (!replyContent || replyContent.trim() === '') {
            throw new Error("Reply content cannot be empty");
        }

        setLoading(true);
        if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.gmail) {
                throw new Error("Gmail API client not initialized properly");
            }
        }
        if (!window.gapi.auth2) {
            throw new Error("Google Auth2 not initialized");
        }

        const authInstance = window.gapi.auth2.getAuthInstance();
        if (!authInstance) {
            throw new Error("Google Auth instance not available");
        }

        const currentUser = authInstance.currentUser.get();
        if (!currentUser) {
            throw new Error("No Google user found");
        }

        if (!currentUser.isSignedIn()) {
            throw new Error("No Google user is signed in");
        }
        const originalMessage = await window.gapi.client.gmail.users.messages.get({
            userId: 'me',
            id: emailId,
            format: 'metadata',
            metadataHeaders: ['Subject', 'From', 'To', 'Message-ID', 'References']
        });

        if (!originalMessage || !originalMessage.result || !originalMessage.result.payload) {
            throw new Error("Failed to fetch original message details");
        }

        const headers = originalMessage.result.payload.headers || [];
        const subjectHeader = headers.find(header => header.name === 'Subject');
        const fromHeader = headers.find(header => header.name === 'From');
        const messageIdHeader = headers.find(header => header.name === 'Message-ID');
        const referencesHeader = headers.find(header => header.name === 'References');
        let subject = subjectHeader?.value || 'No Subject';
        if (!subject.toLowerCase().startsWith('re:')) {
            subject = `Re: ${subject}`;
        }
        let toEmail = '';
        let toName = '';
        if (fromHeader?.value) {
            const matches = fromHeader.value.match(/(.*)<(.*)>/);
            if (matches && matches.length >= 3) {
                toName = matches[1].trim();
                toEmail = matches[2].trim();
            } else {
                toEmail = fromHeader.value.trim();
            }
        }

        if (!toEmail) {
            throw new Error("Could not determine recipient email address");
        }
        let references = '';
        if (messageIdHeader?.value) {
            references = referencesHeader?.value ?
                `${referencesHeader.value} ${messageIdHeader.value}` :
                messageIdHeader.value;
        }
        const boundary = `boundary_${Date.now().toString(16)}`;
        const profile = currentUser.getBasicProfile();

        if (!profile) {
            throw new Error("Could not get user profile");
        }

        const senderEmail = profile.getEmail();
        if (!senderEmail) {
            throw new Error("Could not determine sender email address");
        }

        const email = [
            `From: ${senderEmail}`,
            `To: ${toName ? `${toName} <${toEmail}>` : toEmail}`,
            `Subject: ${subject}`,
            `In-Reply-To: ${messageIdHeader?.value || ''}`,
            `References: ${references}`,
            'MIME-Version: 1.0',
            `Content-Type: multipart/alternative; boundary="${boundary}"`,
            '',
            `--${boundary}`,
            'Content-Type: text/plain; charset=UTF-8',
            '',
            replyContent.replace(/<[^>]*>/g, ''),
            '',
            `--${boundary}`,
            'Content-Type: text/html; charset=UTF-8',
            '',
            replyContent,
            '',
            `--${boundary}--`
        ].join('\r\n');
        const encodedEmail = btoa(email)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        await window.gapi.client.gmail.users.messages.send({
            userId: 'me',
            resource: {
                raw: encodedEmail,
                threadId: threadId
            }
        });

        return true;
    } catch (error) {
        setError(`Failed to send Gmail reply: ${error.message}`);
        return false;
    } finally {
        setLoading(false);
    }
};